import React, {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {select, scaleBand, scaleLinear, axisBottom, max as d3Max} from 'd3'
import {Colors, Typography, Spacing} from '../../assets/styles'
import D3Chart from './D3Chart'

// A large left gutter holds the wrapped category labels; the rest matches the base chart's conventions.
const MARGIN = {top: 8, right: 24, bottom: 30, left: 200}
const BAR_HEIGHT = 34
const MIN_BAR_PX = 2 // keep a sliver visible for tiny-but-nonzero values
const LABEL_LINE_HEIGHT = 1.1 // em

/**
 * Round `max` up to a friendly axis maximum divisible by `tickCount`.
 */
function niceMax(max, tickCount) {
  if (!max || max <= 0) {
    return tickCount
  }
  const roughStep = max / tickCount
  const pow = Math.pow(10, Math.floor(Math.log10(roughStep)))
  const candidates = [1, 2, 2.5, 5, 10].map(m => m * pow)
  const step = candidates.find(c => c >= roughStep) || candidates[candidates.length - 1]
  return step * tickCount
}

// Right-aligned word-wrap for a d3 <text> selection, capped at 2 lines, vertically centered on the band.
function wrapRightAligned(textSelection, width) {
  textSelection.each(function () {
    const text = select(this)
    const words = text.text().split(/\s+/).reverse()
    const x = text.attr('x')
    const y = text.attr('y')
    const lines = []
    let line = []
    let word
    // first pass: split into lines that fit `width`
    const probe = text.append('tspan').attr('x', x)
    while ((word = words.pop())) {
      line.push(word)
      probe.text(line.join(' '))
      if (probe.node().getComputedTextLength() > width && line.length > 1) {
        line.pop()
        lines.push(line.join(' '))
        line = [word]
      }
    }
    lines.push(line.join(' '))
    probe.remove()

    // second pass: render up to 2 lines, vertically centered around the band midpoint
    const shown = lines.slice(0, 2)
    const startDy = -((shown.length - 1) / 2) * LABEL_LINE_HEIGHT + 0.32 // 0.32em ≈ vertical centering fudge
    text.text(null)
    shown.forEach((l, i) => {
      text
        .append('tspan')
        .attr('x', x)
        .attr('y', y)
        .attr('dy', `${startDy + i * LABEL_LINE_HEIGHT}em`)
        .text(l)
    })
  })
}

function HorizontalBarChartD3(props) {
  const {
    data,
    onBarClick,
    selectedId,
    tickCount = 5,
    formatValue = v => `${v}`,
    axisMax: axisMaxProp,
    barColor = Colors.rxrTint25Color,
    highlightColor = Colors.rxrTintColor,
  } = props

  const graphID = useRef(props.id || `hbar-${Math.random().toString(36).substring(7)}`)

  // Latest callback/formatter read at draw time so inline props needn't be redraw triggers.
  const onBarClickRef = useRef(onBarClick)
  const formatValueRef = useRef(formatValue)
  onBarClickRef.current = onBarClick
  formatValueRef.current = formatValue

  // Remove the body-level tooltip when the chart unmounts.
  useEffect(() => () => select(`#tooltip-${graphID.current}`).remove(), [])

  // Draw onto the sized <g> the base chart provides. `canvas.width`/`canvas.height` are the inner area.
  // The base's x/y scales are vertical-oriented, so a horizontal chart ignores them and builds its own.
  const handleReady = ([canvas]) => {
    if (!canvas) {
      return
    }
    const clickable = typeof onBarClickRef.current === 'function'
    const innerWidth = Math.max(0, canvas.width)
    const innerHeight = canvas.height

    // one fresh body-level tooltip per chart instance
    select(`#tooltip-${graphID.current}`).remove()
    const tooltip = select('body')
      .append('div')
      .attr('id', `tooltip-${graphID.current}`)
      .attr(
        'style',
        `position:absolute; z-index:10; display:none; pointer-events:none; text-align:left;
         font-size:${Typography.fontSizeSmall}px; background:${Colors.rxrWhiteColor}; color:${Colors.rxrBlackColor};
         border:1px solid ${Colors.rxrGreyColor}; border-radius:4px; padding:${Spacing.spaceExtraExtraSmall}px ${Spacing.spaceSmall}px;
         box-shadow:${Colors.shadowSoft};`,
      )

    const y = scaleBand()
      .domain(data.map(d => d.id))
      .range([0, innerHeight])
    const dataMax = d3Max(data, d => d.value) || 0
    const axisMax = axisMaxProp || niceMax(dataMax, tickCount)
    const x = scaleLinear().domain([0, axisMax]).range([0, innerWidth])

    // gridlines
    canvas
      .append('g')
      .selectAll('line')
      .data(x.ticks(tickCount))
      .join('line')
      .attr('x1', d => x(d))
      .attr('x2', d => x(d))
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .attr('stroke', Colors.rxrGreyColor)
      .attr('stroke-width', 1)

    // bars
    canvas
      .append('g')
      .selectAll('rect')
      .data(data)
      .join('rect')
      .attr('x', 0)
      .attr('y', d => y(d.id) + (y.bandwidth() - BAR_HEIGHT) / 2)
      .attr('height', BAR_HEIGHT)
      .attr('width', d => (d.value > 0 ? Math.max(MIN_BAR_PX, x(d.value)) : 0))
      .attr('fill', d => (selectedId != null && d.id === selectedId ? highlightColor : barColor))
      .style('cursor', clickable ? 'pointer' : 'default')
      .on('mousemove', (event, d) => {
        tooltip
          .style('display', 'block')
          .style('left', `${event.pageX + 12}px`)
          .style('top', `${event.pageY - 12}px`)
          .html(`${d.label}: ${formatValueRef.current(d.value)}`)
      })
      .on('mouseleave', () => tooltip.style('display', 'none'))
      .on('click', (event, d) => {
        if (clickable) {
          onBarClickRef.current(d)
        }
      })

    // x-axis
    canvas
      .append('g')
      .attr('transform', `translate(0, ${innerHeight})`)
      .attr('style', `font-size:${Typography.fontSizeSmall}px; color:${Colors.rxrDarkGreyColor}`)
      .call(axisBottom(x).ticks(tickCount).tickFormat(formatValueRef.current).tickSizeOuter(0))

    // y-axis category labels: right-aligned in the gutter, wrapped to <=2 lines, bold when selected
    canvas
      .append('g')
      .selectAll('text')
      .data(data)
      .join('text')
      .attr('x', -12)
      .attr('y', d => y(d.id) + y.bandwidth() / 2)
      .attr('text-anchor', 'end')
      .attr('fill', Colors.rxrBlackColor)
      .attr('font-size', Typography.fontSizeMedium)
      .attr('font-weight', d => (selectedId != null && d.id === selectedId ? Typography.heavyFontWeight : Typography.lightFontWeight))
      .text(d => d.label)
      .call(wrapRightAligned, MARGIN.left - 16)
  }

  return <D3Chart id={`hbar-wrap-${graphID.current}`} onReady={handleReady} margin={MARGIN} immediate />
}

HorizontalBarChartD3.propTypes = {
  id: PropTypes.string,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    }),
  ).isRequired,
  onBarClick: PropTypes.func,
  selectedId: PropTypes.string,
  tickCount: PropTypes.number,
  formatValue: PropTypes.func,
  axisMax: PropTypes.number,
  barColor: PropTypes.string,
  highlightColor: PropTypes.string,
}

export default HorizontalBarChartD3
